import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import { WishListItem } from 'tradera/apps/my-tradera/wish-list/components/wish-list-item';
import { useInView } from 'react-intersection-observer';
import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';
import HeartFilledIcon from '@tradera/blueprint/theme/icons2/heart-fill.svg?jsx';
import styles from './wish-list-side-sheet.module.scss';
import { Trans, useTranslator } from 'tradera/lang/translate';
import { WishListEmpty } from 'tradera/apps/tradera/start-page/components/wish-list/wish-list-empty';
import {
    endpoints,
    useGetWishListEndlessQuery,
    useWishListParams
} from 'tradera/state/services/wish-list-api';
import { ScrollIntoViewOnChange } from 'tradera/components/scroll-into-view-on-change/scroll-into-view-on-change';
import { SERVER_FETCHED_NUMBER_WISHLIST_ITEMS } from 'tradera/apps/tradera/start-page/components/wish-list/constants';
import { useResetState } from 'tradera/apps/tradera/components/use-reset-state';
import { Space } from 'tradera/components/atoms/space/space';
import { Divider } from 'tradera/components/divider/divider';
import { wishlist } from 'tradera/state/items/actions';
import { useAppDispatch } from 'tradera/state/hooks';
import { SOURCE_SCREEN } from 'tradera/services/google-tagmanager/constants';
import { BidHistoryModal } from 'tradera/components/bid-history/bid-history-modal';

const defaultPageSize = SERVER_FETCHED_NUMBER_WISHLIST_ITEMS;

type UseWishListDataProps = {
    statusFilter?: string;
    pageSize?: number;
    skip?: boolean;
};

export const useWishListData = ({
    statusFilter,
    pageSize = defaultPageSize,
    skip = false
}: UseWishListDataProps) => {
    // By using the data from endpoints we can utilize SSR:d data, but then we
    // need to call the hook below anyway to get in-client interactivity like pagination.
    const {
        currentData: {
            page: latestPageMaybe,
            items: wishListItems = [],
            totalCount = 0
        } = {}
    } = endpoints.getWishListEndless.useQueryState(
        useWishListParams({ statusFilter, pageSize })
    );
    const latestPage = latestPageMaybe ?? 1;
    const [page, setPage] = useResetState(latestPage);

    const { isLoading, isFetching } = useGetWishListEndlessQuery(
        useWishListParams({ page, statusFilter, pageSize }),
        { skip }
    );
    const nextPage = useCallback(() => {
        setPage((p) => p + 1);
    }, [setPage]);

    const isAllItemsFetched = latestPage * pageSize >= totalCount;

    return {
        wishListItems,
        isLoading,
        isFetching,
        isAllItemsFetched,
        page,
        nextPage
    };
};

type WishListItemsProps = {
    filter: string;
    searchTerm: string;
    setShowMayBeMoreResultsText: (show: boolean) => void;
    setWishlistEmpty: (empty: boolean) => void;
};

export const WishListItems = ({
    filter,
    searchTerm,
    setShowMayBeMoreResultsText,
    setWishlistEmpty
}: WishListItemsProps) => {
    const { t } = useTranslator();
    const dispatch = useAppDispatch();
    const [bidHistoryItemId, setBidHistoryItemId] = useState<number | null>(
        null
    );
    const {
        isLoading,
        isFetching,
        wishListItems,
        isAllItemsFetched,
        page,
        nextPage
    } = useWishListData({ statusFilter: filter });

    const { ref: loadingMoreRef, inView } = useInView();

    const hasSearchTerm = searchTerm.trim() !== '';

    useEffect(() => {
        if (hasSearchTerm || isFetching || !inView || isAllItemsFetched) return;

        nextPage();
    }, [inView, isFetching, hasSearchTerm, isAllItemsFetched, nextPage]);

    useEffect(() => {
        if (!hasSearchTerm || isAllItemsFetched)
            setShowMayBeMoreResultsText(false);

        const timeoutCheck = setTimeout(() => {
            if (!hasSearchTerm || isAllItemsFetched)
                setShowMayBeMoreResultsText(false);
            else if (!isAllItemsFetched) setShowMayBeMoreResultsText(true);
        }, 1000);

        return () => clearTimeout(timeoutCheck);
    }, [hasSearchTerm, setShowMayBeMoreResultsText, isAllItemsFetched]);

    const visibleWishListItems = useMemo(() => {
        if (!hasSearchTerm) return wishListItems;

        const fuseOptions = {
            ignoreLocation: true,
            threshold: 0.4,
            keys: ['shortDescription', 'sellerAlias']
        };
        const fuse = new Fuse(wishListItems, fuseOptions);

        return fuse.search(searchTerm).map((results) => results.item);
    }, [hasSearchTerm, searchTerm, wishListItems]);

    useEffect(() => {
        if (!isFetching && wishListItems.length === 0) setWishlistEmpty(true);
        else setWishlistEmpty(false);
    }, [isFetching, wishListItems, setWishlistEmpty]);

    const handleWishlistClick = (itemId: number) => {
        dispatch(
            wishlist(itemId, {
                sectionGaName: 'Wish list item',
                gaSourceScreen: SOURCE_SCREEN.WISHLIST
            })
        );
    };

    return (
        <>
            <ScrollIntoViewOnChange deps={[filter]} />
            {!isFetching && wishListItems.length === 0 && (
                <div className="d-flex flex-column align-items-center mx-2 flex-grow-1 justify-content-center">
                    {filter === 'all' ? (
                        <>
                            <WishListEmpty className="mb-3" width="120px" />
                            <h4 className="mb-0 text-center">
                                {t('wishlist_sidesheet_empty_title')}
                            </h4>
                            <p className="text-center">
                                <Trans
                                    i18nKey="wishlist_sidesheet_empty_content_Trans"
                                    components={{
                                        icon: (
                                            <HeartFilledIcon
                                                className={`icon icon-md icon-danger ${styles.heart}`}
                                            />
                                        )
                                    }}
                                />
                            </p>
                        </>
                    ) : (
                        <h4 className="text-center">
                            {t('wishlist_sidesheet_no_items_with_filter')}
                        </h4>
                    )}
                </div>
            )}
            {isLoading && page === 1 ? (
                <LoadingIcon className="icon icon-xl icon-spin m-auto w-100" />
            ) : (
                <>
                    <Space
                        className="px-2 px-md-0"
                        direction="vertical"
                        gap={1}>
                        {visibleWishListItems?.map((item) => {
                            return (
                                <Fragment key={item.itemId}>
                                    <WishListItem
                                        item={item}
                                        setBidHistoryItemId={
                                            setBidHistoryItemId
                                        }
                                        handleWishlistClick={
                                            handleWishlistClick
                                        }
                                    />
                                    <Divider dark />
                                </Fragment>
                            );
                        })}
                    </Space>
                    {isFetching && (
                        <div className="text-center">
                            <LoadingIcon className="icon icon-lg icon-spin m-auto" />
                        </div>
                    )}
                    <div ref={loadingMoreRef} />
                    <BidHistoryModal
                        itemId={bidHistoryItemId ?? 0}
                        show={!!bidHistoryItemId}
                        onClose={() => setBidHistoryItemId(null)}
                        isBuyer={true}
                    />
                </>
            )}
        </>
    );
};
